// apps/client/src/game/interactions/remove.ts (M1-T15) — the mayor's Remove verb (spec §4.4, D-39): only for the mayor, only with
// empty hands, only on MAYOR_REMOVABLE objects outside every plot, at the lowest priority. A never removes: it opens `confirmRemove`.
// Furniture another resident placed in their yard is an item, not a world object: M1-T16's yardTidy handles that.
import { getEntry, tuning, type StringKey } from '@whiskerton/content';
import { rotateFootprint, type ObjectId } from '@whiskerton/shared';
import { MAYOR_REMOVABLE, canRemoveObject } from '@whiskerton/shared/rules/permissions';
import type { Interaction } from '../interact';
import { objectsOf } from '../../net/handlers/nature';

/** In front of you: the cosine of the angle between your facing and the footprint's nearest point (shake.ts, pickUp.ts). */
const FRONT_COS = 0.2;

export const interaction: Interaction = {
  id: 'remove',
  find(ctx) {
    // `isMayor` on the store is a unit-test seam; in the game the role comes from `residents` and `you`.
    const s = ctx.store as typeof ctx.store & { isMayor?: boolean };
    const isMayor = s.isMayor ?? (s.you ? s.residents[s.you.resident]?.role === 'mayor' : false);
    if (!isMayor || ctx.space !== 'out' || ctx.tool !== null || !s.map) return null;
    const fx = Math.sin(ctx.f), fy = Math.cos(ctx.f);
    let best: { id: string; d: number; x: number; y: number } | null = null;
    for (const o of objectsOf(ctx.game)) {
      if (!MAYOR_REMOVABLE.includes(o.kind) || !canRemoveObject({ isMayor }, o, s.map.plots)) continue;
      const e = getEntry(o.catalogId);
      const { w, d: depth } = rotateFootprint(e && e.kind === 'world' ? e.footprint : [1, 1], o.rot);
      const x = o.x + w / 2, y = o.y + depth / 2;
      const d = Math.hypot(x - ctx.pos.x, y - ctx.pos.y);
      if (d > tuning.town.interactRange + (Math.max(w, depth) - 1) / 2) continue;   // reach is measured from the footprint's edge
      // Facing: toward the footprint's nearest point (its centre when you stand on it), as the other A verbs face their target.
      const nx = Math.min(Math.max(ctx.pos.x, o.x), o.x + w) - ctx.pos.x, ny = Math.min(Math.max(ctx.pos.y, o.y), o.y + depth) - ctx.pos.y;
      const edge = Math.hypot(nx, ny) > 1e-6, [ax, ay] = edge ? [nx, ny] : [x - ctx.pos.x, y - ctx.pos.y], len = Math.hypot(ax, ay);
      if ((edge || len > 0.3) && (ax * fx + ay * fy) / len < FRONT_COS) continue;
      if (!best || d < best.d) best = { id: o.id, d, x, y };
    }
    return best && { id: best.id, at: { x: best.x, y: best.y }, verb: 'hud.verbRemove', priority: 1 };
  },
  run(ctx, c) {
    const o = objectsOf(ctx.game).find((x) => x.id === c.id);
    const e = o && getEntry(o.catalogId);
    ctx.store.openScreen('confirmRemove', { obj: c.id as ObjectId, name: (e?.name ?? 'common.notice') as StringKey });
  },
};
